import React, { useState, useEffect, useRef } from "react";
import { Bar, BarChart, CartesianGrid, ReferenceArea, XAxis } from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../ui/chart";
import { ChartZoomControls, dayLabel, dayStart, useDailyChartZoom } from "./ChartZoom";

export const description = "An interactive bar chart";

export default function FramesStat(props) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
  if (props.startDate) {
    const d = new Date(props.startDate);
    // Formato “DD/MM/YYYY HH:mm”
    const formatted = d.toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    setStartDate(formatted);
  }

    if (props.endDate) {
    const d = new Date(props.endDate);
    // Formato “DD/MM/YYYY HH:mm”
    const formatted = d.toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    setEndDate(formatted);
  }
  }, [props.startDate, props.endDate]);

    const [activeChart] = React.useState("value");

    const chartData = React.useMemo(() => {
      const data = (props.frameDetections ?? []).map((detection) => {
        const ts = dayStart(detection?.timestamp);
        return { ts, date: dayLabel(ts, detection?.timestamp), value: detection?.nFrameDetections || 0 };
      });
      return data.every((d) => Number.isFinite(d.ts)) ? data.sort((a, b) => a.ts - b.ts) : data;
    }, [props.frameDetections]);

    const { visibleData, controls, dragHandlers, dragArea } = useDailyChartZoom(chartData);
    
    const total = React.useMemo(() => {
      return {
        value: chartData.reduce((acc, curr) => acc + curr.value, 0),
      };
    }, [chartData]);
    
    const chartConfig = {
      views: {
        label: "Frames",
      },
      value: {
        label: "Frames",
        color: "hsl(var(--chart-1))",
      },
    };
    
  return (
    <Card>
      <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
          <CardTitle>Frame Detections</CardTitle>
          <CardDescription> 
          {startDate === endDate ? `Showing total frame detections from ${startDate}`: `Showing total frame detections from ${startDate} to ${endDate}`}
          </CardDescription>
        </div>
        <div className="flex">
          {["value"].map((key) => {
            const chart = key;
            return (
              <button
                key={chart}
                className="relative  flex flex-1 flex-col justify-center gap-1 border-t px-6 py-4 text-left even:border-l data-[active=true]:bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6"  
              >
                <span className="text-xs text-muted-foreground">
                  {chartConfig[chart].label}
                </span>
                <span className="text-lg font-bold leading-none sm:text-3xl">
                  {total[key].toLocaleString()}
                </span>
              </button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="px-2 sm:p-6">
        <ChartZoomControls className="mb-3 mt-4 sm:mt-0" {...controls} />
        <ChartContainer
          config={chartConfig}
          data-swapy-no-drag className="chart-zoomable aspect-auto h-[280px] sm:h-[400px] w-full"
        >
          <BarChart
            accessibilityLayer
            data={visibleData}
            {...dragHandlers}
            margin={{
              left: 12,
              right: 12,
              bottom: 25
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              className="m-2"
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={8}
              label={{
                value: "Date (DD/MM)",
                position: "insideBottom",
                offset: -20,
                style: { textAnchor: "middle", fill: "#888"  }
              }}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  className="w-[150px]"
                  nameKey="value"
                />
              }
            />
            <Bar dataKey={activeChart} fill={`var(--color-${activeChart})`} />
            {dragArea?.start && dragArea?.end && (
              <ReferenceArea x1={dragArea.start.date} x2={dragArea.end.date} fillOpacity={0.12} strokeOpacity={0} />
            )}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
